import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import LocationStep from "@/components/LocationStep";
import {
  Map, Plus, Trash2, FileText, Download, CreditCard, ChevronRight, ChevronLeft,
  Check, AlertCircle, TrendingUp, Sparkles, RefreshCw, Lock, Unlock, EyeOff,
  Building2, Home, Store, Users
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const TOPOGRAFIAS = ["Plano", "Semiplano", "Accidentado"];
const CONSERVACION = ["Excelente", "Buena", "Regular"];
const SCENARIO_OPTIONS = [
  { id: "casa", label: "Casa Unifamiliar", icon: Home },
  { id: "desarrollo", label: "Pequeño Desarrollo (2-4)", icon: Building2 },
  { id: "edificio", label: "Edificio de Departamentos", icon: Building2 },
  { id: "comercial", label: "Comercial / Local", icon: Store },
];

const CREDIT_PACKAGES = {
  single: { reports: 1, price: 99, label: "1 Reporte" },
  pack_5: { reports: 5, price: 299, label: "5 Reportes", savings: "40%" },
  pack_10: { reports: 10, price: 499, label: "10 Reportes", savings: "50%" },
};

const TerrenoModule = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState("new");
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [credits, setCredits] = useState(null);
  const [history, setHistory] = useState([]);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [resultData, setResultData] = useState(null);

  const [subject, setSubject] = useState({
    referencia: "",
    superficie_m2: "",
    frente_ml: "",
    niveles: 2,
    topografia: "Plano",
    precio_venta: "",
    costo_construccion_m2: "12000",
    zona: "",
    uso_suelo: "",
    usos_permitidos: "",
    observaciones: "",
    ubicacion_factor: 1.0,
  });

  const [testigos, setTestigos] = useState([
    { tipo: "Terreno", precio_lista: "", m2_terreno: "", frente_ml: "", topografia: "Plano", zona_fuente: "", ubicacion_factor: 1.0 },
  ]);

  const [activeScenarios, setActiveScenarios] = useState(["casa", "desarrollo", "edificio", "comercial"]);

  // Geolocation state (new step 2)
  const [location, setLocation] = useState({
    lat: "", lng: "", colonia: "", municipio: "", estado: "",
    full_address: "", nse: "", infraestructura_notes: ""
  });

  const fetchCredits = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/terreno/credits`, { headers: { Authorization: `Bearer ${token}` } });
      setCredits(r.data);
    } catch (e) { console.error(e); }
  }, [token]);

  const fetchHistory = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/terreno/history`, { headers: { Authorization: `Bearer ${token}` } });
      setHistory(r.data.terrenos || []);
    } catch (e) { console.error(e); }
  }, [token]);

  useEffect(() => { fetchCredits(); fetchHistory(); }, [fetchCredits, fetchHistory]);

  const buildPayload = () => ({
    subject: {
      ...subject,
      superficie_m2: parseFloat(subject.superficie_m2) || 0,
      frente_ml: parseFloat(subject.frente_ml) || 0,
      niveles: parseInt(subject.niveles) || 1,
      precio_venta: parseFloat(subject.precio_venta) || 0,
      costo_construccion_m2: parseFloat(subject.costo_construccion_m2) || 12000,
      ubicacion_factor: parseFloat(subject.ubicacion_factor) || 1.0,
      // Location enrichment
      lat: location.lat ? parseFloat(location.lat) : null,
      lng: location.lng ? parseFloat(location.lng) : null,
      colonia: location.colonia || subject.zona || "",
      municipio: location.municipio || "",
      estado: location.estado || "",
      full_address: location.full_address || "",
      nse: location.nse || "",
      infraestructura_notes: location.infraestructura_notes || "",
    },
    testigos: testigos.map(t => ({
      ...t,
      precio_lista: parseFloat(t.precio_lista) || 0,
      m2_terreno: parseFloat(t.m2_terreno) || 0,
      frente_ml: parseFloat(t.frente_ml) || 0,
      m2_construidos: parseFloat(t.m2_construidos) || 0,
      antiguedad_anos: parseInt(t.antiguedad_anos) || 0,
      ubicacion_factor: parseFloat(t.ubicacion_factor) || 1.0,
    })),
    active_scenarios: activeScenarios,
  });

  const fetchPreview = useCallback(async () => {
    setPreviewLoading(true); setPreviewData(null);
    try {
      const r = await axios.post(`${API}/terreno/preview`, buildPayload(), { headers: { Authorization: `Bearer ${token}` } });
      setPreviewData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al calcular preview");
    } finally { setPreviewLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, subject, testigos, activeScenarios]);

  useEffect(() => {
    if (currentStep === 4) fetchPreview();
    else setPreviewData(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  const validateStep = (step) => {
    if (step === 1) return subject.referencia && subject.superficie_m2 && subject.precio_venta && subject.zona && subject.uso_suelo;
    if (step === 2) return !!(location.lat && location.lng) || !!location.colonia;
    if (step === 3) return testigos.length >= 1 && testigos.every(t => t.precio_lista && (t.m2_terreno || t.m2_construidos));
    return true;
  };

  const handleAddTestigo = () => {
    if (testigos.length < 5) {
      setTestigos([...testigos, { tipo: "Terreno", precio_lista: "", m2_terreno: "", frente_ml: "", topografia: "Plano", zona_fuente: "", ubicacion_factor: 1.0 }]);
    }
  };
  const handleRemoveTestigo = (i) => testigos.length > 1 && setTestigos(testigos.filter((_, idx) => idx !== i));
  const updateTestigo = (i, field, val) => {
    const next = [...testigos]; next[i][field] = val; setTestigos(next);
  };

  const handleUnlock = async () => {
    if (!credits || credits.total_available <= 0) { setShowPurchaseModal(true); return; }
    setIsLoading(true);
    try {
      const r = await axios.post(`${API}/terreno/create`, buildPayload(), { headers: { Authorization: `Bearer ${token}` } });
      setResultData(r.data); setCurrentStep(5); fetchCredits(); fetchHistory();
      toast.success("Análisis generado");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al generar análisis");
      if (e.response?.status === 402) setShowPurchaseModal(true);
    } finally { setIsLoading(false); }
  };

  const handlePurchase = async (pkg) => {
    setIsLoading(true);
    try {
      await axios.post(`${API}/terreno/purchase-credits?package_type=${pkg}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Paquete agregado"); fetchCredits(); setShowPurchaseModal(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al procesar la compra");
    } finally { setIsLoading(false); }
  };

  const downloadPdf = async (id) => {
    try {
      const r = await axios.get(`${API}/terreno/download/${id}`, { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url; link.setAttribute("download", `terreno_${id}.pdf`);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Error al descargar PDF"); }
  };

  const formatMxn = (n) => `$${Math.round(n || 0).toLocaleString('es-MX')}`;

  // ==== STEP 1 ====
  const renderStep1 = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="md:col-span-2">
        <Label>Referencia / Dirección *</Label>
        <Input value={subject.referencia} onChange={e => setSubject({ ...subject, referencia: e.target.value })} placeholder="Ej: Lote 23, Av. Reforma 1200" data-testid="terreno-ref" />
      </div>
      <div>
        <Label>Superficie total (m²) *</Label>
        <Input type="number" value={subject.superficie_m2} onChange={e => setSubject({ ...subject, superficie_m2: e.target.value })} placeholder="500" />
      </div>
      <div>
        <Label>Frente lineal (ml)</Label>
        <Input type="number" value={subject.frente_ml} onChange={e => setSubject({ ...subject, frente_ml: e.target.value })} placeholder="15" />
      </div>
      <div>
        <Label>Niveles permitidos</Label>
        <Input type="number" value={subject.niveles} onChange={e => setSubject({ ...subject, niveles: e.target.value })} placeholder="3" />
      </div>
      <div>
        <Label>Topografía</Label>
        <Select value={subject.topografia} onValueChange={v => setSubject({ ...subject, topografia: v })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>{TOPOGRAFIAS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <Label>Precio de venta (MXN) *</Label>
        <Input type="number" value={subject.precio_venta} onChange={e => setSubject({ ...subject, precio_venta: e.target.value })} placeholder="12000000" />
      </div>
      <div>
        <Label>Costo construcción ($/m²)</Label>
        <Input type="number" value={subject.costo_construccion_m2} onChange={e => setSubject({ ...subject, costo_construccion_m2: e.target.value })} placeholder="12000" />
      </div>
      <div>
        <Label>Zona / Colonia *</Label>
        <Input value={subject.zona} onChange={e => setSubject({ ...subject, zona: e.target.value })} placeholder="Polanco" />
      </div>
      <div>
        <Label>Uso de suelo *</Label>
        <Input value={subject.uso_suelo} onChange={e => setSubject({ ...subject, uso_suelo: e.target.value })} placeholder="H4 / Mixto" />
      </div>
      <div className="md:col-span-2">
        <Label>Usos permitidos</Label>
        <Input value={subject.usos_permitidos} onChange={e => setSubject({ ...subject, usos_permitidos: e.target.value })} placeholder="Habitacional, Mixto, Comercial" />
      </div>
      <div className="md:col-span-2">
        <Label>Observaciones</Label>
        <Textarea value={subject.observaciones} onChange={e => setSubject({ ...subject, observaciones: e.target.value })} placeholder="Servicios, accesos, restricciones..." />
      </div>
      <div>
        <Label>Factor zonal ubicación (0.90 - 1.15)</Label>
        <Input type="number" step="0.05" value={subject.ubicacion_factor} onChange={e => setSubject({ ...subject, ubicacion_factor: e.target.value })} />
      </div>
    </div>
  );

  // ==== STEP 2 ====
  const renderStep2 = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold">Testigos Comparables</h3>
          <p className="text-sm text-slate-500">Mínimo 1, máximo 5 (terrenos o inmuebles construidos)</p>
        </div>
        {testigos.length < 5 && <Button variant="outline" size="sm" onClick={handleAddTestigo}><Plus className="w-4 h-4 mr-1" /> Agregar</Button>}
      </div>
      {testigos.map((t, i) => (
        <Card key={i} className="border-l-4 border-l-primary">
          <CardContent className="pt-4 space-y-3">
            <div className="flex items-center justify-between">
              <Badge variant="outline">Testigo {i + 1}</Badge>
              {testigos.length > 1 && <Button variant="ghost" size="sm" onClick={() => handleRemoveTestigo(i)}><Trash2 className="w-4 h-4 text-red-500" /></Button>}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs">Tipo</Label>
                <Select value={t.tipo} onValueChange={v => updateTestigo(i, "tipo", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Terreno">Terreno</SelectItem><SelectItem value="Inmueble">Inmueble</SelectItem></SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Precio lista (MXN) *</Label>
                <Input type="number" value={t.precio_lista} onChange={e => updateTestigo(i, "precio_lista", e.target.value)} placeholder="11500000" />
              </div>
              {t.tipo === "Terreno" ? (
                <>
                  <div>
                    <Label className="text-xs">m² terreno *</Label>
                    <Input type="number" value={t.m2_terreno} onChange={e => updateTestigo(i, "m2_terreno", e.target.value)} placeholder="480" />
                  </div>
                  <div>
                    <Label className="text-xs">Frente (ml)</Label>
                    <Input type="number" value={t.frente_ml} onChange={e => updateTestigo(i, "frente_ml", e.target.value)} placeholder="14" />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <Label className="text-xs">m² construidos *</Label>
                    <Input type="number" value={t.m2_construidos || ""} onChange={e => updateTestigo(i, "m2_construidos", e.target.value)} placeholder="350" />
                  </div>
                  <div>
                    <Label className="text-xs">Antigüedad (años)</Label>
                    <Input type="number" value={t.antiguedad_anos || ""} onChange={e => updateTestigo(i, "antiguedad_anos", e.target.value)} placeholder="10" />
                  </div>
                  <div>
                    <Label className="text-xs">m² terreno</Label>
                    <Input type="number" value={t.m2_terreno} onChange={e => updateTestigo(i, "m2_terreno", e.target.value)} placeholder="500" />
                  </div>
                  <div>
                    <Label className="text-xs">Conservación</Label>
                    <Select value={t.conservacion || "Buena"} onValueChange={v => updateTestigo(i, "conservacion", v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{CONSERVACION.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </>
              )}
              <div>
                <Label className="text-xs">Topografía</Label>
                <Select value={t.topografia} onValueChange={v => updateTestigo(i, "topografia", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{TOPOGRAFIAS.map(tp => <SelectItem key={tp} value={tp}>{tp}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Factor ubicación</Label>
                <Input type="number" step="0.05" value={t.ubicacion_factor} onChange={e => updateTestigo(i, "ubicacion_factor", e.target.value)} placeholder="1.00" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Zona / Fuente</Label>
                <Input value={t.zona_fuente} onChange={e => updateTestigo(i, "zona_fuente", e.target.value)} placeholder="Polanco / Inmuebles24" />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}

      <div className="pt-2">
        <Label className="text-sm font-medium">Escenarios a evaluar</Label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
          {SCENARIO_OPTIONS.map(s => {
            const active = activeScenarios.includes(s.id);
            return (
              <Button key={s.id} variant={active ? "default" : "outline"} size="sm"
                onClick={() => setActiveScenarios(active ? activeScenarios.filter(x => x !== s.id) : [...activeScenarios, s.id])}>
                <s.icon className="w-4 h-4 mr-1" /> {s.label}
              </Button>
            );
          })}
        </div>
      </div>
    </div>
  );

  // ==== STEP 3 (PAYWALL PREVIEW) ====
  const renderStep3 = () => {
    const hasCredits = (credits?.total_available || 0) > 0;
    return (
      <div className="space-y-4">
        <Card className="border-2 border-primary/40 bg-gradient-to-br from-primary/5 via-white to-primary/10" data-testid="terreno-paywall">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" /><CardTitle className="text-base">Preview gratuito de Potencial de Terreno</CardTitle></div>
              <Badge variant="secondary" className="flex items-center gap-1"><EyeOff className="w-3 h-3" /> Vista parcial</Badge>
            </div>
            <CardDescription>Cálculo determinístico (homologación + escenarios). Te mostramos un rango aproximado — los valores exactos se desbloquean con 1 crédito.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {previewLoading ? (
              <div className="flex items-center justify-center py-8 text-slate-500"><RefreshCw className="w-5 h-5 animate-spin mr-2" /> Calculando preview...</div>
            ) : previewData ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white/80 backdrop-blur rounded-lg p-4 border">
                    <p className="text-xs text-slate-500 mb-1">Valor base homologado (rango ±10%)</p>
                    <p className="text-2xl font-bold text-primary">{formatMxn(previewData.precio_m2_base_range.low)} – {formatMxn(previewData.precio_m2_base_range.high)}</p>
                    <p className="text-xs text-slate-400 mt-1">MXN/m² — basado en {previewData.testigos_count} testigos homologados</p>
                  </div>
                  <div className="bg-white/80 backdrop-blur rounded-lg p-4 border">
                    <p className="text-xs text-slate-500 mb-1">Escenario Recomendado</p>
                    <p className="text-lg font-bold text-slate-700">⭐ {previewData.escenario_recomendado.label}</p>
                    <p className="text-sm text-slate-600 mt-1">ROI estimado: <strong>{previewData.escenario_recomendado.roi_range.low}% – {previewData.escenario_recomendado.roi_range.high}%</strong></p>
                    <p className="text-xs text-slate-400">Inversión aprox: {formatMxn(previewData.escenario_recomendado.inversion_estimada_range.low)} – {formatMxn(previewData.escenario_recomendado.inversion_estimada_range.high)}</p>
                  </div>
                </div>

                <div className="bg-slate-50 rounded-lg p-4 space-y-2 border border-dashed">
                  <p className="text-sm font-medium text-slate-700 flex items-center gap-2"><Lock className="w-4 h-4" /> Se desbloquea con 1 crédito:</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-slate-600">
                    <div className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-green-500" /> Valor base homologado exacto</div>
                    <div className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-green-500" /> 4 escenarios completos (ROI, utilidad, inversión)</div>
                    <div className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-green-500" /> Tabla de homologación detallada</div>
                    <div className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-green-500" /> Costos notariales (ISAI, IVA, ISR, etc.)</div>
                    <div className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-green-500" /> Dictamen técnico con IA</div>
                    <div className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-green-500" /> PDF profesional con sello digital</div>
                  </div>
                </div>

                <div className={`rounded-lg p-4 flex items-start gap-3 ${hasCredits ? "bg-green-50 border border-green-200" : "bg-amber-50 border border-amber-200"}`}>
                  {hasCredits ? (
                    <><Unlock className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-medium text-green-800">Tienes {credits?.total_available} crédito{credits?.total_available !== 1 ? "s" : ""} disponible{credits?.total_available !== 1 ? "s" : ""}</p>
                        <p className="text-sm text-green-700">{(credits?.free_reports_remaining || 0) > 0 ? `${credits.free_reports_remaining} gratuito${credits.free_reports_remaining !== 1 ? "s" : ""} este mes + ${credits?.purchased_credits || 0} comprado${credits?.purchased_credits !== 1 ? "s" : ""}.` : `${credits?.purchased_credits || 0} comprado${credits?.purchased_credits !== 1 ? "s" : ""}.`} Al desbloquear se descontará 1 crédito.</p>
                      </div></>
                  ) : (
                    <><Lock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-medium text-amber-800">No tienes créditos disponibles</p>
                        <p className="text-sm text-amber-700">Compra un paquete desde ${CREDIT_PACKAGES.single.price} MXN para desbloquear el reporte completo.</p>
                        <Button size="sm" className="mt-2 bg-amber-600 hover:bg-amber-700 text-white" onClick={() => setShowPurchaseModal(true)}><CreditCard className="w-4 h-4 mr-1" /> Ver paquetes</Button>
                      </div></>
                  )}
                </div>
              </>
            ) : <div className="text-center py-6 text-slate-500 text-sm">Completa los pasos anteriores para ver el preview.</div>}
          </CardContent>
        </Card>
      </div>
    );
  };

  // ==== RESULTS ====
  const renderResults = () => (
    <div className="space-y-6">
      <div className="text-center py-6 bg-gradient-to-r from-primary/10 to-primary/5 rounded-xl">
        <Sparkles className="w-12 h-12 text-primary mx-auto mb-3" />
        <h2 className="text-2xl font-bold text-slate-800">Análisis Completado</h2>
        <p className="text-slate-500">Tu reporte profesional está listo</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-2 border-primary"><CardContent className="pt-4 text-center">
          <p className="text-xs text-slate-500 mb-1">Valor base homologado</p>
          <p className="text-xl font-bold text-primary">{formatMxn(resultData?.precio_m2_base)}/m²</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4 text-center">
          <p className="text-xs text-slate-500 mb-1">Escenario Recomendado</p>
          <p className="text-lg font-semibold">⭐ {resultData?.recommended_summary?.label}</p>
          <p className="text-sm text-slate-600">ROI {resultData?.recommended_summary?.roi}%</p>
        </CardContent></Card>
      </div>
      <Card>
        <CardHeader><CardTitle className="text-sm flex items-center gap-2"><TrendingUp className="w-4 h-4 text-green-500" /> Comparativa de Escenarios</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-3">
            {(resultData?.scenarios || []).map(s => {
              const isRec = s.id === resultData?.escenario_recomendado;
              return (
                <div key={s.id} className={`p-3 rounded-lg border ${isRec ? "border-primary bg-primary/5" : "border-slate-200"}`}>
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium">{s.label} {isRec && <Badge className="ml-2 bg-primary text-slate-900">Recomendado</Badge>}</span>
                    <span className="text-lg font-bold text-green-600">ROI {s.roi}%</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs text-slate-600">
                    <div>Inversión: <strong>{formatMxn(s.inversion_total)}</strong></div>
                    <div>Venta est: <strong>{formatMxn(s.valor_venta_est)}</strong></div>
                    <div>Utilidad: <strong className="text-green-700">{formatMxn(s.utilidad_bruta)}</strong></div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {resultData?.dictamen && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2"><Sparkles className="w-4 h-4 text-primary" /> Dictamen Técnico (IA)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{resultData.dictamen}</div>
          </CardContent>
        </Card>
      )}
      <div className="flex gap-3 justify-center">
        <Button onClick={() => downloadPdf(resultData.analisis_id)} className="bg-primary hover:bg-primary/90"><Download className="w-4 h-4 mr-2" /> Descargar PDF</Button>
        <Button variant="outline" onClick={() => { setCurrentStep(1); setResultData(null); setLocation({ lat: "", lng: "", colonia: "", municipio: "", estado: "", full_address: "", nse: "", infraestructura_notes: "" }); }}><RefreshCw className="w-4 h-4 mr-2" /> Nuevo Análisis</Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2"><Map className="w-6 h-6 text-primary" /> Potencial de Terreno</h1>
          <p className="text-slate-500">Análisis de potencial de desarrollo + 4 escenarios + ROI + dictamen IA</p>
        </div>
        <div className="flex items-center gap-3">
          <Card className="px-4 py-2"><div className="flex items-center gap-2"><FileText className="w-5 h-5 text-primary" /><div><p className="text-xs text-slate-500">Reportes Disponibles</p><p className="font-bold text-lg">{credits?.total_available || 0}</p></div></div></Card>
          <Button onClick={() => setShowPurchaseModal(true)}><CreditCard className="w-4 h-4 mr-2" /> Comprar</Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="new">Nuevo Análisis</TabsTrigger>
          <TabsTrigger value="history">Historial ({history.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="new" className="mt-4">
          {currentStep < 5 ? (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>
                    {currentStep === 1 && "Paso 1: Datos del Terreno"}
                    {currentStep === 2 && "Paso 2: Geolocalización"}
                    {currentStep === 3 && "Paso 3: Testigos & Escenarios"}
                    {currentStep === 4 && "Paso 4: Preview & Desbloqueo"}
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    {[1, 2, 3, 4].map(s => (
                      <div key={s} className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${currentStep === s ? "bg-primary text-white" : currentStep > s ? "bg-green-500 text-white" : "bg-slate-200 text-slate-500"}`}>
                        {currentStep > s ? <Check className="w-4 h-4" /> : s}
                      </div>
                    ))}
                  </div>
                </div>
                <Progress value={(currentStep / 4) * 100} className="h-1 mt-2" />
              </CardHeader>
              <CardContent>
                {currentStep === 1 && renderStep1()}
                {currentStep === 2 && <LocationStep location={location} onChange={setLocation} />}
                {currentStep === 3 && renderStep2()}
                {currentStep === 4 && renderStep3()}
              </CardContent>
              <CardFooter className="flex justify-between">
                <Button variant="outline" onClick={() => setCurrentStep(Math.max(1, currentStep - 1))} disabled={currentStep === 1}>
                  <ChevronLeft className="w-4 h-4 mr-1" /> Anterior
                </Button>
                {currentStep < 4 ? (
                  <Button onClick={() => setCurrentStep(currentStep + 1)} disabled={!validateStep(currentStep)} data-testid="terreno-next">
                    {currentStep === 3 ? "Ver Preview" : "Siguiente"} <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                ) : (credits?.total_available || 0) > 0 ? (
                  <Button onClick={handleUnlock} disabled={isLoading || previewLoading} className="bg-primary hover:bg-primary/90" data-testid="terreno-unlock">
                    {isLoading ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Generando...</> : <><Unlock className="w-4 h-4 mr-2" /> Desbloquear Reporte (1 crédito)</>}
                  </Button>
                ) : (
                  <Button onClick={() => setShowPurchaseModal(true)} className="bg-primary hover:bg-primary/90">
                    <CreditCard className="w-4 h-4 mr-2" /> Comprar crédito
                  </Button>
                )}
              </CardFooter>
            </Card>
          ) : renderResults()}
        </TabsContent>
        <TabsContent value="history" className="mt-4">
          <Card>
            <CardHeader><CardTitle>Historial de Análisis</CardTitle></CardHeader>
            <CardContent>
              {history.length === 0 ? (
                <div className="text-center py-8 text-slate-500"><FileText className="w-12 h-12 mx-auto mb-3 opacity-50" /><p>No tienes análisis anteriores</p></div>
              ) : (
                <div className="space-y-3">
                  {history.map(t => (
                    <div key={t.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50">
                      <div>
                        <p className="font-medium">{t.subject?.referencia}</p>
                        <p className="text-sm text-slate-500">{t.subject?.zona} | {t.subject?.superficie_m2} m² | Recomendado: {t.result?.escenario_recomendado}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <p className="text-sm text-slate-500">{new Date(t.created_at).toLocaleDateString('es-MX')}</p>
                        <Button variant="outline" size="sm" onClick={() => downloadPdf(t.id)}><Download className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={showPurchaseModal} onOpenChange={setShowPurchaseModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Comprar Reportes de Terreno</DialogTitle><DialogDescription>Selecciona un paquete</DialogDescription></DialogHeader>
          <div className="space-y-3 py-4">
            {Object.entries(CREDIT_PACKAGES).map(([key, pkg]) => (
              <div key={key} className="flex items-center justify-between p-4 border rounded-lg hover:border-primary cursor-pointer" onClick={() => handlePurchase(key)}>
                <div>
                  <p className="font-medium">{pkg.label}</p>
                  {pkg.savings && <Badge variant="secondary" className="mt-1">Ahorra {pkg.savings}</Badge>}
                </div>
                <div className="text-right">
                  <p className="font-bold text-lg">${pkg.price} MXN</p>
                  <p className="text-xs text-slate-500">${(pkg.price / pkg.reports).toFixed(0)}/reporte</p>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TerrenoModule;
